import React, { FormEvent } from 'react';
import { connect } from 'react-redux';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { RouteComponentProps } from 'react-router';
import { ThunkDispatch } from 'redux-thunk';
import ReactDataSheet from 'react-datasheet';
import { toast } from 'react-toastify';
import { AppState } from '../../../../store/store.types';
import { PromoCodeActionTypes } from '../../types/promocode.types';
import {
  AffectedRatePlanModel,
  RateModel,
  RatePlanCarModel,
  RatePlanModel,
  RatePlanPeriodModel,
  RatePlanRequest,
  RateRequest,
  RateType
} from '../../../../api/pricing/pricing.types';
import { getCarModelsList } from '../../../setup/actions/carmodel.actions';
import { addNewRatePlan, editRatePlan, getRatePlan, getRatePlanList } from '../../actions/rateplan.actions';
import { getDeskGroupList } from '../../../setup/actions/deskgroup.actions';
import { CarModelModel, DeskGroupModel, ProductModel } from '../../../../api/core/core.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { DATE_FORMAT, SelectedOption, StatusType } from '../../../../api/api.types';
import { TextInput } from '../../../../components/forms/Input';
import Select from '../../../../components/forms/Select';
import { getProductList } from '../../../setup/actions/product.actions';
import Checkbox from '../../../../components/forms/Checkbox';
import SheetRenderer, { CellRenderer, GridElement, RowRenderer } from '../../../../components/ratecomponents/DataSheet';
import { getRatePlanPeriods } from '../../../core/actions/rateplanperiod.actions';
import { getRateTableColumns } from '../../../../helpers/rateplan.helper';
import { PricingApi } from '../../../../api/api.apiclient';
import AffectedRatePlansModal from '../../../../components/modals/AffectedRatePlansModal';
import RatePlanAdjustmentModal from '../../../../components/modals/RateAdjustmentModal';
import dayjs, { Dayjs } from 'dayjs';
import { IntervalDatePickerEvent } from '../../../../components/forms/DatePicker';
import { WithTranslation, withTranslation } from 'react-i18next';
import CalendarDateRangePicker from '../../../../components/forms/CalendarDateRangePicker';
import { dateOrderCorrect } from '../../../../helpers/date.helper';
import { showErrorToast } from '../../../../components/Toaster';

interface RatePlanLocationProps {
  id: string;
}

interface RatePlanDetailsProps extends RouteComponentProps<RatePlanLocationProps>, WithTranslation {
  getSelectedRatePlan: (id: number) => void;
  addRatePlan: (request: RatePlanRequest) => void;
  editRatePlan: (request: RatePlanRequest, id: number) => void;
  getRatePlanList: () => void;
  getDeskGroupList: () => void;
  getCarModelsList: () => void;
  getProductList: () => void;
  getRatePlanPeriodsList: () => void;
  selectedRatePlan: RatePlanModel | null;
  products: ProductModel[];
  deskGroups: DeskGroupModel[];
  ratePlanPeriods: RatePlanPeriodModel[];
  isAdjustment?: boolean;
  isNew?: boolean;
  isCopy?: boolean;
  postSuccess: boolean | null;
  carModels: CarModelModel[];
  lastFetch: number | null;
  user: UserModel | null;
  selectedProductId: number | null;
  selectedpickupDeskGroupId: number | null;
}

interface RatePlanDetailsState {
  grid: GridElement[][];
  columns: { label: string; width: string }[];
  selectedLines: boolean[];
  standardPlan: RatePlanModel[];
  allRowsSelected: boolean;
  dateOrderIncorrect: boolean;
  affectedPlans: AffectedRatePlanModel[];
  request?: RatePlanRequest;
  modalOpen: boolean;
  isAdjustmentModalOpen: boolean;
  dateInputClicked: {
    fromDate: boolean,
    toDate: boolean
  };
}

class RatePlanDetails extends React.Component<RatePlanDetailsProps, RatePlanDetailsState> {

  readonly state = {
    columns: [],
    grid: [],
    selectedLines: [],
    standardPlan: [],
    allRowsSelected: false,
    dateOrderIncorrect: false,
    affectedPlans: [],
    request: undefined,
    modalOpen: false,
    isAdjustmentModalOpen: false,
    dateInputClicked: {
      fromDate: false,
      toDate: false
    }
  };

  form = FormBuilder.group({
    name: [ '', Validators.required ],
    validFrom: [ null, Validators.required ],
    validTo: [ null, Validators.required ],
    coreProductId: [ null, Validators.required ],
    pickupDeskGroupId: [ null, Validators.required ],
    active: [ false ]
  });

  async componentDidMount() {
    const { id } = this.props.match.params;

    if ((!this.props.isNew || this.props.isAdjustment || this.props.isCopy) && !this.props.selectedRatePlan && id !== '0') {
      this.props.getSelectedRatePlan(Number(id));
    }

    if (!this.props.deskGroups.length) {
      this.props.getDeskGroupList();
    }

    if (!this.props.products.length) {
      this.props.getProductList();
    }

    if (!this.props.carModels.length) {
      this.props.getCarModelsList();
    }

    if (!this.props.ratePlanPeriods.length) {
      this.props.getRatePlanPeriodsList();
    }

    if (this.props.isNew && !this.props.isAdjustment) {
      this.updateFormForNewStandardPlan();
    }

    if (((this.props.isCopy || this.props.isAdjustment) && this.props.selectedRatePlan)
      || (!this.props.isNew && this.props.selectedRatePlan)) {
      this.updateFormValue();
    }

    if (this.props.isAdjustment) {
      if (!this.props.isNew && !this.props.isCopy && this.props.selectedRatePlan && this.props.selectedRatePlan.standardPlanId) {
        await this.getStandardPlan(this.props.selectedRatePlan.standardPlanId);
      } else if (this.props.selectedRatePlan) {
        await this.setState({ standardPlan: [ this.props.selectedRatePlan ] });
      }
    }

    if (this.props.ratePlanPeriods.length !== 0) {
      this.assignColumns(this.props.ratePlanPeriods);
    }

    this.assignRatesToTableGrid();
  }

  componentDidUpdate(prevProps: RatePlanDetailsProps) {
    if (this.props.postSuccess) {
      this.props.history.push('/rates/rateplans/');
    }

    if (!this.state.columns.length && this.props.ratePlanPeriods.length) {
      this.assignColumns(this.props.ratePlanPeriods);
    }

    if (this.props.selectedRatePlan && (!prevProps.selectedRatePlan || this.props.selectedRatePlan !== prevProps.selectedRatePlan)) {
      this.updateFormValue();
      this.assignRatesToTableGrid();
    }
  }

  updateFormForNewStandardPlan = () => {
    this.form.patchValue({
      coreProductId: this.props.selectedProductId,
      pickupDeskGroupId: this.props.selectedpickupDeskGroupId
    });
  };

  updateFormValue = () => {
    if (!this.props.selectedRatePlan) {
      return;
    }

    let validFrom = dayjs(this.props.selectedRatePlan.validFrom).isBefore(dayjs()) && (this.props.isAdjustment || this.props.isCopy)
      ? dayjs()
      : dayjs(this.props.selectedRatePlan.validFrom);

    let validTo = JSON.parse(JSON.stringify(validFrom));
    validTo = (this.props.isAdjustment && this.props.isNew) ? dayjs(validTo).add(1, 'week') : dayjs(this.props.selectedRatePlan.validTo);
    if (this.props.isCopy) {
      validFrom = dayjs(JSON.parse(JSON.stringify(this.props.selectedRatePlan.validTo))).add(1, 'day');
      validTo = JSON.parse(JSON.stringify(validFrom));
      validTo = dayjs(validTo).add(1, 'week');
    }

    this.form.patchValue({
      name: this.getPlanDefaultName(),
      coreProductId: this.props.selectedRatePlan.coreProductId,
      validFrom,
      validTo,
      pickupDeskGroupId: this.props.selectedRatePlan.pickupDeskGroupId,
      active: this.props.selectedRatePlan.statusType === StatusType.ACTIVE
    });
  };

  getPlanDefaultName = () => {
    if (this.props.isAdjustment && this.props.isNew) {
      return 'Adjustment';
    }

    if (this.props.selectedRatePlan) {
      const name = this.props.selectedRatePlan.name;
      return this.props.isCopy ? `${name} Copy` : name;
    }

    return '';
  };

  onDateChange = (field: IntervalDatePickerEvent, date?: Dayjs) => {
    this.form.patchValue({
      [ `valid${field.valueOf()}` ]: date
    });

    if (date && field === IntervalDatePickerEvent.FROM && !this.form.controls.validTo.value && this.props.isNew) {
      const dateCopy = dayjs(JSON.parse(JSON.stringify(date)));
      this.form.patchValue({
        validTo: dateCopy.add(1, 'week')
      });
    }

    if (this.form.controls.validFrom.value && this.form.controls.validTo.value) {
      if (this.form.controls.validTo.value.isBefore(this.form.controls.validFrom.value)) {
        this.setState({ dateOrderIncorrect: true });
      } else this.setState({ dateOrderIncorrect: false });
    }
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid || this.state.dateOrderIncorrect) {
      return;
    }

    const rates = this.getRatesFromTable();

    if (!rates.length) {
      return;
    }

    const request: RatePlanRequest = this.composeRequest(form, rates);

    if (!this.props.isAdjustment) {
      let id: number | undefined = undefined;
      if (this.props.selectedRatePlan) {
        id = this.props.selectedRatePlan.id;
      }
      PricingApi.RatePlan.getAffectedRatePlans(request, id).then((response) => {
        if (!response.data.length) {
          this.confirmSubmit(request);
        } else {
          this.setState({ request, modalOpen: true, affectedPlans: response.data });
        }
      });
    } else {
      this.confirmSubmit(request);
    }

  };

  confirmSubmit = (request: RatePlanRequest) => {
    if (this.props.selectedRatePlan && !this.props.isNew && !this.props.isCopy) {

      const { id } = this.props.match.params;
      this.props.editRatePlan(request, Number(id));
    } else {

      this.props.addRatePlan(request);
    }

    this.setState({ modalOpen: false, affectedPlans: [], request: undefined });
  };

  composeRequest = (form: any, rates: RateRequest[]) => {
    const request: RatePlanRequest = {
      rateType: RateType.BASE_RATE,
      coreProductId: form.coreProductId.value,
      validFrom: form.validFrom.value.format(DATE_FORMAT),
      validTo: form.validTo.value.format(DATE_FORMAT),
      name: form.name.value,
      pickupDeskGroupId: form.pickupDeskGroupId.value,
      rates,
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE
    };

    if (this.props.isAdjustment && this.state.standardPlan.length) {
      const standardPlan: RatePlanModel = this.state.standardPlan[ 0 ];
      const group = this.props.deskGroups.find((dg) => dg.id === standardPlan.pickupDeskGroupId);
      // const locationGroup: PickupDeskGroupModel = { groupId: standardPlan.pickupDeskGroupId, groupName: group ? group.name : 'copy' };

      request.rateType = RateType.ADJUSTMENT;
      request.standardPlanId = standardPlan.id;
      request.coreProductId = standardPlan.coreProductId;
      request.pickupDeskGroupId = standardPlan.pickupDeskGroupId;
    } else if (this.props.isAdjustment && !this.state.standardPlan.length) {
      request.rateType = RateType.ADJUSTMENT;
    }

    return request;
  };

  getStandardPlan = async (id: number) => {
    const standardPlan = await PricingApi.RatePlan.getOneRatePlan(id);

    this.setState({ standardPlan: [ standardPlan.data ] });
    this.assignRatesToTableGrid();
  };

  onCancel = () => {
    this.props.history.push('/rates/rateplans/');
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  patchLocationGroups = (values: any) => {
    const newValues: number[] = [];

    if (values) {
      newValues.push(Number(values.value));
    }

    this.form.patchValue({ locationGroups: newValues });
  };

  getCarModelName = (carModelId: number) => {
    const carModel = this.props.carModels.find((cm) => cm.id === carModelId);
    if (carModel) return carModel.name;

    return String(carModelId);
  };

  getRateMatrix = (ratePlan: RatePlanModel) => {
    const rates = ratePlan.rates.sort((a, b) => a.rowNumber - b.rowNumber);
    const ratesMatrix: RateModel[][] = [ [] ];

    let rowNumber = 1;
    let activeRow = 1;
    rates.forEach((rate) => {
      if (rate.rowNumber !== rowNumber) {
        rowNumber = rate.rowNumber;
        activeRow++;
        ratesMatrix.push([ rate ]);
      } else ratesMatrix[ activeRow - 1 ].push(rate);
    });

    return ratesMatrix;
  };

  assignColumns = (ratePlanPeriods: RatePlanPeriodModel[]) => {
    const isAdjustment = this.props.isAdjustment && this.state.standardPlan.length !== 0;
    const columns = getRateTableColumns(ratePlanPeriods, isAdjustment);

    this.setState({ columns });
  };

  getRatePlanPeriods = async () => this.props.getRatePlanPeriodsList();

  assignRatesToTableGrid = async () => {
    if ((this.props.isNew && !this.props.isCopy && !this.props.isAdjustment) || (this.props.isAdjustment && !this.state.standardPlan.length)) {
      if (!this.props.ratePlanPeriods.length) await this.getRatePlanPeriods();

      this.setState({ grid: this.getEmptyGridRows(1, 2 + this.props.ratePlanPeriods.length) });

    } else {
      let ratePlan;
      if (this.props.isAdjustment && this.state.standardPlan.length) {
        ratePlan = this.state.standardPlan[ 0 ];
      } else ratePlan = this.props.selectedRatePlan;

      if (ratePlan && ratePlan.rates.length) {
        const ratesMatrix = this.getRateMatrix(ratePlan);
        const grid: GridElement[][] = [];

        ratesMatrix.forEach((row) => {
          const gridRow = this.composeGridRow(row);

          if (row.length) {
            grid.push(gridRow);
          }
        });

        if (this.props.isAdjustment) {
          const selectedLines = [];

          for (let i = 0; i < grid.length; i++) {
            selectedLines.push(false);
          }

          const adjustment = this.props.selectedRatePlan;
          if (adjustment) {
            const adjustmentMatrix = this.getRateMatrix(adjustment);

            adjustmentMatrix.forEach((r) => {
              const adjustmentRow = this.composeGridRow(r);

              grid.forEach((row, index) => {
                if (adjustmentRow.length) {
                  if (row[ 0 ].value === adjustmentRow[ 0 ].value && row[ 1 ].value === adjustmentRow[ 1 ].value) {
                    grid[ index ] = adjustmentRow;
                    selectedLines[ index ] = true;
                  }
                }
              });
            });
          }

          this.setState({ grid, selectedLines });
        } else {
          grid.push(this.getEmptyGridRows(1, 2 + this.props.ratePlanPeriods.length)[ 0 ]);
          this.setState({ grid });
        }
      }
    }
  };

  composeGridRow = (row: RateModel[]) => {
    if (row.length) {
      const gridRow = [ {
        value: row[ 0 ].carModelInfo.acrissCode,
        readOnly: this.props.isAdjustment
      }, { value: this.getCarModelName(row[ 0 ].carModelInfo.carModelId), readOnly: this.props.isAdjustment } ];

      this.props.ratePlanPeriods.forEach((rpp) => {
        const rate = row.find((r) => r.ratePlanPeriod.daysFrom === rpp.daysFrom);

        if (rate) gridRow.push({ value: String(rate.baseAmount), readOnly: false });
        else gridRow.push({ value: String(''), readOnly: false });
      });

      return gridRow;
    }

    return [];
  };

  getRatesFromTable = () => {
    const rates: RateRequest[] = [];
    const ratePlanPeriods = this.props.ratePlanPeriods;
    const sortedRatePlanPeriods = ratePlanPeriods.sort((a: RatePlanPeriodModel, b: RatePlanPeriodModel) => a.daysFrom - b.daysFrom);
    let errorsFound = false;
    const { t } = this.props;

    this.state.grid.forEach((row: GridElement[], index: number) => {
      if (!this.isRowClear(row)) {

        if (!this.rowContainsEmptyCell(row)) {
          const acrissCode = row[ 0 ].value;
          const modelName = row[ 1 ].value;

          let ratePlanPeriodIndex = 0;
          const rateBaseAmounts: { baseAmount: number; ratePlanPeriodId: number }[] = [];
          for (let i = 2; i < row.length; i++) {
            // eslint-disable-next-line no-restricted-globals
            if (row[ i ] && row[ i ].value && !isNaN(Number(row[ i ].value))) {
              rateBaseAmounts.push({
                baseAmount: Number(row[ i ].value),
                ratePlanPeriodId: sortedRatePlanPeriods[ ratePlanPeriodIndex ].id
              });
            }

            ratePlanPeriodIndex++;
          }

          if (!rateBaseAmounts.length) errorsFound = true;

          if (acrissCode && modelName) {
            const carModel = this.getCarModel(acrissCode, modelName);

            if (carModel) {
              rateBaseAmounts.forEach((rba) => {
                const carModelInfo: RatePlanCarModel = {
                  carModelId: carModel.id,
                  acrissCode: carModel.acrissCode,
                  minDriverAge: carModel.minDriverAge,
                  youngDriverAge: carModel.youngDriverAge
                };
                const rate: RateRequest = {
                  ratePlanPeriodId: rba.ratePlanPeriodId,
                  baseAmount: rba.baseAmount,
                  rowNumber: index + 1,
                  carModelInfo
                };

                if (!this.props.isAdjustment) rates.push(rate);

                else if (this.props.isAdjustment && this.state.selectedLines.length) {
                  if (this.state.selectedLines[ index ] === true) {
                    rates.push(rate);
                  }
                }
              });
            } else {
              errorsFound = true;
              toast(t('warning.rate_plan.car_model_not_found', {
                modelName: modelName,
                acrissCode: acrissCode
              }), { type: 'error', hideProgressBar: true });
            }
          } else {
            errorsFound = true;
            showErrorToast(t('warning.rate_plan.car_model_incomplete'));
          }
        } else if (this.state.selectedLines[ index ] === false) {
          errorsFound = true;
          showErrorToast(t('warning.rate_plan.row_contains_empty_cell'));
        }
      }
    });

    if (errorsFound) return [];

    return rates;
  };

  rowContainsEmptyCell = (row: GridElement[]) => {
    let emptyCellFound = false;

    let index = 0;
    while (!emptyCellFound && index < row.length) {
      const cell = row[ index ];
      if (cell && cell.value === '') emptyCellFound = true;

      index++;
    }

    return emptyCellFound;
  };

  isRowClear = (row: GridElement[]) => {
    let rowIsClear = true;

    let index = 0;
    while (rowIsClear && index < row.length) {
      const cell = row[ index ];
      if (cell && cell.value !== '') rowIsClear = false;

      index++;
    }

    return rowIsClear;
  };

  getCarModel = (acrissCode: string, modelName: string) => {
    const carModel = this.props.carModels.find((c) => (c.acrissCode === acrissCode) && (c.name === modelName));
    if (carModel) return carModel;

    return undefined;
  };

  getEmptyGridRows = (count: number, columns: number) => {
    const grid: GridElement[][] = [];

    for (let i = 1; i <= count; i++) {
      const gridElements: GridElement[] = [];

      for (let j = 1; j <= columns; j++) {
        gridElements.push({ value: '' });
      }

      grid.push(gridElements);
    }

    return grid;
  };

  splitOverflow = (overflow: any[]) => {
    let rowNumber = 0;
    const overflowRows: any[][] = [];
    let rowOfElements: any[] = [];

    overflow.forEach((o) => {
      if (rowNumber !== 0 && rowNumber !== o.row) {
        overflowRows.push(rowOfElements);
        rowNumber = o.row;
        rowOfElements = [];
      }

      if (rowNumber === 0) rowNumber = o.row;

      rowOfElements.push(o);
    });
    overflowRows.push(rowOfElements);

    return overflowRows;
  };

  renderSheet = (props: any) => <SheetRenderer onAllRowSelectClick={this.onAllRowsSelectedChange}
                                               allSelected={this.state.allRowsSelected}
                                               isAdjustment={this.props.isAdjustment && this.state.standardPlan.length !== 0}
                                               columns={this.state.columns} {...props} />;

  renderAdjustmentCell = (props: any) => {
    const lines = this.state.selectedLines;

    return <CellRenderer isAdjustment selectedRows={lines} {...props} />;
  };

  handleSelectChanged = (index: number, isSelected: boolean) => {
    const selections: boolean[] = [ ...this.state.selectedLines ];
    selections[ index ] = isSelected;
    this.setState({ selectedLines: selections, allRowsSelected: false });
  };

  onAllRowsSelectedChange = () => {
    const selectedLines = this.state.selectedLines.map(() => !this.state.allRowsSelected);
    this.setState({ allRowsSelected: !this.state.allRowsSelected, selectedLines });
  };

  renderRow = (props: any) => <RowRenderer onAllRowSelectClick={this.onAllRowsSelectedChange}
                                           isSelected={this.state.selectedLines[ props.row ]}
                                           onSelectChanged={this.handleSelectChanged} {...props} />;

  isNumeric = (value: string): boolean => !new RegExp(/[^\d]/g).test(value.trim());

  onStandardRateCellChange = (changes: any[], overflow: any) => {
    const grid: GridElement[][] = this.state.grid.map((row) => [ ...row ]);
    changes.forEach(({
                       row, col, value
                     }) => {
      let newValue: string = value;
      const checkValue = newValue.replace(' ', '');
      if (this.isNumeric(checkValue)) {
        newValue = newValue.replace(' ', '');
      }
      if (value.includes(',')) {
        newValue = newValue.replace(',', '.');
      }
      newValue = newValue.trim();

      grid[ row ][ col ] = { ...grid[ row ][ col ], value: newValue };
    });

    if (overflow) {
      const overFlowRows = this.splitOverflow(overflow);
      overFlowRows.forEach((overflows) => {
        const rowOfElements: GridElement[] = [];

        overflows.forEach((o) => {
          rowOfElements.push({ value: o.value });
        });

        if (rowOfElements.length < this.state.columns.length) {
          while (rowOfElements.length < this.state.columns.length) rowOfElements.push({ value: '' });
        }

        grid.push(rowOfElements);
      });
      grid.push(this.getEmptyGridRows(1, this.state.columns.length)[ 0 ]);
    }

    if (!this.isRowClear(grid[ grid.length - 1 ])) grid.push(this.getEmptyGridRows(1, this.state.columns.length)[ 0 ]);

    if (this.isRowClear(grid[ grid.length - 1 ]) && this.isRowClear(grid[ grid.length - 2 ])) grid.pop();

    this.setState({ grid });
  };

  onAdjustmentCellChange = (changes: any[]) => {
    const grid: GridElement[][] = this.state.grid.map((row) => [ ...row ]);
    changes.forEach(({
                       row, col, value
                     }) => {
      if (this.state.selectedLines[ row ] === true) {
        let newValue = value;
        newValue = newValue.replace(' ', '');
        if (value.includes(',')) {
          newValue = newValue.replace(',', '.');
        }

        grid[ row ][ col ] = { ...grid[ row ][ col ], value: newValue };
      }
    });

    this.setState({ grid });
  };

  getProductValue = () => {
    const productValue = this.form.controls.coreProductId.value;

    if (productValue) {
      const product = this.props.products.find((p) => p.id === productValue);

      if (product) return { value: String(product.id), label: product.name };
    }

    return undefined;
  };

  getLocationGroupValue = () => {
    const locationGroupValue = this.form.controls.pickupDeskGroupId.value;
    if (locationGroupValue) {
      const deskGroup = this.props.deskGroups.find((dg) => dg.id === locationGroupValue);

      if (deskGroup) return { value: String(deskGroup.id), label: deskGroup.name };
    }
    return undefined;
  };


  getPageHeader = () => {
    const { t } = this.props;

    let header = t('page_title.rate_plan.add');

    if (!this.props.isNew) header = t('page_title.rate_plan.edit');
    if (this.props.isCopy) header = t('page_title.rate_plan.copy');

    return header.concat(this.props.isAdjustment ? ' ' + t('page_title.rate_plan.adjustment') : ' ' +
      t('page_title.rate_plan.standard_rate'));
  };

  canEditProduct = () => {
    if (!this.props.isAdjustment && (this.props.isNew || this.props.isCopy)) {
      return true;
    }
    return !!(this.props.isAdjustment && !this.state.standardPlan.length);


  };

  getProductName = (productId: number) => {
    const product = this.props.products.find((p) => p.id === productId);

    if (product) return product.name;

    return String(productId);
  };

  getDeskGroupName = (deskGroupId: number) => {
    const deskGroup = this.props.deskGroups.find((d) => d.id === deskGroupId);

    if (deskGroup) return deskGroup.name;

    return String(deskGroupId);
  };

  getRatePlanPeriodsForModal = () => {
    const columns: { label: string; width: string }[] = this.state.columns;

    const periodWithIndex: { period: string; columnNumber: number }[] = [];

    columns.forEach((e, index) => {
      if (index >= 2) periodWithIndex.push({ period: String(e.label), columnNumber: index });
    });

    return periodWithIndex;
  };

  getCarClassesForModal = () => {
    const rows: GridElement[][] = this.state.grid;

    const activeCarClasses: { acrissCode: string; name: string; rowNumber: number }[] = [];

    if (this.state.selectedLines.length === 0) {
      rows.forEach((r, index) => {
        if (r[ 0 ].value !== undefined && String(r[ 1 ].value).length !== 0) {
          activeCarClasses.push({ acrissCode: String(r[ 0 ].value), name: String(r[ 1 ].value), rowNumber: index });
        }
      });
    } else {
      rows.forEach((r, index) => {
        if (this.state.selectedLines[ index ]) {
          activeCarClasses.push({ acrissCode: String(r[ 0 ].value), name: String(r[ 1 ].value), rowNumber: index });
        }
      });
    }

    return activeCarClasses;
  };

  adjustRates = (selectedRows: number[], selectedColumns: number[], adjustBy: number) => {
    const gridCopy: GridElement[][] = JSON.parse(JSON.stringify(this.state.grid));
    const multiplier = adjustBy / 100;

    gridCopy.forEach((r, index) => {
      if (selectedRows.includes(index)) {
        r.forEach((c, inx) => {
          if (selectedColumns.includes(inx)) {
            c.value = String((Number(c.value) + (Number(c.value) * multiplier)).toFixed(2).replace(/\.00$/, ''));
          }
        });
      }
    });

    this.setState({ grid: gridCopy });
  };

  render() {
    if (!this.props.isNew && !this.props.selectedRatePlan) {
      return null;
    }

    const canEditProduct = this.canEditProduct();
    const selectedRatePlanId = this.props.selectedRatePlan ? this.props.selectedRatePlan.id : undefined;

    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1 className="mb-2 detail-view-header-label">{this.getPageHeader()}</h1>
            </div>
          </div>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label className="mandatory-field">* </label>
                    <label className="rate-detail-label">{t('l.rate_plan.name')}:</label>
                  </div>
                  <div className="col-4 d-flex align-items-center justify-content-start edit-form-input">
                    <FieldControl
                      name="name"
                      render={TextInput}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    {selectedRatePlanId && !this.props.isNew && !this.props.isCopy && (
                      <div className="form-control-static col-4 no-brake-text d-flex align-items-center pl-0">
                        {`(ID ${selectedRatePlanId})`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label className="mandatory-field">* </label>
                    <label className="rate-detail-label">{t('l.rate_plan.period')}:</label>
                  </div>
                  <div className="col-2 d-flex row-gap">
                    <div className="col padding-zero">
                      <CalendarDateRangePicker
                        isStartDateInput
                        startDate={this.form.controls.validFrom.value}
                        endDate={this.form.controls.validTo.value}
                        onChange={(val) => {
                          dateOrderCorrect(val, 'valid', this.form);
                          this.form.patchValue({ 'validFrom': val.startDate, 'validTo': val.endDate });
                        }}
                        inputStyle={{ flex: '50%' }}
                        isInvalid={this.form.controls.validFrom.invalid && this.form.submitted}
                        id="1"
                      />
                    </div>
                    <div className="col padding-zero">
                      <CalendarDateRangePicker
                        isStartDateInput={false}
                        startDate={this.form.controls.validFrom.value}
                        endDate={this.form.controls.validTo.value}
                        onChange={(val) => {
                          dateOrderCorrect(val, 'valid', this.form);
                          this.form.patchValue({ 'validFrom': val.startDate, 'validTo': val.endDate });
                        }}
                        inputStyle={{ flex: '50%' }}
                        isInvalid={this.form.controls.validTo.invalid && this.form.submitted}
                        id="2"
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label className="mandatory-field">* </label>
                    <label className="rate-detail-label">{t('l.rate_plan.product')}:</label>
                  </div>
                  <div className="col-4 d-flex align-items-center justify-content-start edit-form-input">
                    {!canEditProduct && (
                      <div className="form-control-static">
                        {this.getProductName(this.form.controls.coreProductId.value)}
                      </div>
                    )}
                    {canEditProduct && (
                      <FieldControl
                        strict={false}
                        name="coreProductId"
                        render={(props) => (
                          <Select
                            value={this.getProductValue()}
                            className="mr-1"
                            placeholder={t('ph.rate_plan.product')}
                            disableClear
                            options={this.props.products.map((p) => ({ value: String(p.id), label: p.name }))}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            label="core product id"
                            onChange={(value) => this.patchField(value as SelectedOption, 'coreProductId')}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
                <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label className="mandatory-field">* </label>
                    <label className="rate-detail-label">{t('l.rate_plan.applies_to')}:</label>
                  </div>
                  <div className="col-4 d-flex align-items-center justify-content-start edit-form-input">
                    {!canEditProduct && this.props.selectedRatePlan && (
                      <div className="form-control-static">
                        {this.getDeskGroupName(this.props.selectedRatePlan.pickupDeskGroupId)}
                      </div>
                    )}
                    {canEditProduct && (
                      <FieldControl
                        strict={false}
                        name="pickupDeskGroupId"
                        render={(props) => (
                          <Select
                            value={this.getLocationGroupValue()}
                            className="mr-1"
                            disableClear
                            placeholder={t('ph.rate_plan.location_group')}
                            // eslint-disable-next-line no-nested-ternary
                            options={this.props.deskGroups.map((d) => ({ value: String(d.id), label: d.name }))}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            label="location group id"
                            onChange={(value) => this.patchField(value as SelectedOption, 'pickupDeskGroupId')}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
                <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                    <label className="mandatory-field">* </label>
                    <label className="rate-detail-label">{t('l.rate_plan.status')}:</label>
                  </div>
                  <div className="col-4 d-flex align-items-center justify-content-start">
                    <FieldControl
                      name="active"
                      render={(props) => <Checkbox {...props} id="statusField" className="mt-1">
                        {t('text.status.active')}</Checkbox>}
                    />
                  </div>
                </div>
              </form>
            )}
          />
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              onClick={() => this.setState({ isAdjustmentModalOpen: true })}
            >
              {t('b.rate_plan.adjust_rates').toUpperCase()}
            </button>
          </div>
          {!this.props.isAdjustment && (
            <div className="mt-3">
              <ReactDataSheet
                data={this.state.grid}
                valueRenderer={(cell: any) => cell.value}
                sheetRenderer={this.renderSheet}
                cellRenderer={CellRenderer}
                onCellsChanged={(changes, overflow) => this.onStandardRateCellChange(changes, overflow)}
              />
            </div>
          )}
          {this.props.isAdjustment && this.state.standardPlan.length === 0 && (
            <div className="mt-3">
              <ReactDataSheet
                data={this.state.grid}
                valueRenderer={(cell: any) => cell.value}
                sheetRenderer={this.renderSheet}
                cellRenderer={CellRenderer}
                onCellsChanged={(changes, overflow) => this.onStandardRateCellChange(changes, overflow)}
              />
            </div>
          )}
          {this.props.isAdjustment && this.state.standardPlan.length !== 0 && (
            <div className="mt-2">
              <ReactDataSheet
                data={this.state.grid}
                valueRenderer={(cell: any) => cell.value}
                sheetRenderer={this.renderSheet}
                cellRenderer={this.renderAdjustmentCell}
                rowRenderer={this.renderRow}
                onCellsChanged={(changes) => this.onAdjustmentCellChange(changes)}
              />
            </div>
          )}
          <div className="row mt-2">
            <div className="col-12 text-right">
              <button type="button" className="btn btn-outline-secondary btn-lg mr-2" onClick={this.onCancel}>
                {t('b.cancel_Generic cancel button translation').toUpperCase()}
              </button>
              <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                {t('b.save_Generic save button translation').toUpperCase()}
              </button>
            </div>
          </div>
        </div>
        {this.state.request && (
          <AffectedRatePlansModal
            ratePlans={this.state.affectedPlans}
            request={this.state.request as unknown as RatePlanRequest}
            isOpen={this.state.modalOpen}
            onConfirm={(request: RatePlanRequest) => this.confirmSubmit(request)}
            onCancel={() => this.setState({ modalOpen: false })}
          />
        )}
        {this.state.grid.length !== 0 && this.state.columns.length !== 0 && (
          <RatePlanAdjustmentModal
            toggle={() => this.setState({ isAdjustmentModalOpen: false })}
            isOpen={this.state.isAdjustmentModalOpen}
            ratePlanPeriods={this.getRatePlanPeriodsForModal()}
            activeCarClasses={this.getCarClassesForModal()}
            onAdjustmentConfirm={(selectedRows: number[], selectedColumns: number[], adjustBy: number) => this.adjustRates(selectedRows, selectedColumns, adjustBy)}
          />
        )}
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedRatePlan: state.rates.ratePlans.selectedRatePlan,
  postSuccess: state.rates.ratePlans.postSuccess,
  lastFetch: state.rates.ratePlans.lastFetch,
  carModels: state.setup.carModels.carModels,
  deskGroups: state.setup.deskGroups.deskGroups,
  ratePlans: state.rates.ratePlans.ratePlans,
  products: state.setup.products.products,
  ratePlanPeriods: state.core.ratePlanPeriods.ratePlanPeriods,
  user: state.authentication.user,
  selectedProductId: state.rates.ratePlans.productId,
  selectedDeskGroupId: state.rates.ratePlans.deskGroupId
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, PromoCodeActionTypes>
) => ({
  getSelectedRatePlan: (id: number) => dispatch(getRatePlan(id)),
  getRatePlanList: () => dispatch(getRatePlanList()),
  addRatePlan: (request: RatePlanRequest) => dispatch(addNewRatePlan(request)),
  editRatePlan: (request: RatePlanRequest, id: number) => dispatch(editRatePlan(id, request)),
  getProductList: () => dispatch(getProductList()),
  getDeskGroupList: () => dispatch(getDeskGroupList()),
  getRatePlanPeriodsList: () => dispatch(getRatePlanPeriods()),
  getCarModelsList: () => dispatch(getCarModelsList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(RatePlanDetails));
